/**
 * One tool execution, recorded exactly once.
 *
 * Two handlers can see the same tool call — the SDK's `onToolCall` and the
 * backend's per-call status part — and they share one "already seen" Set. That
 * pairing has been wrong in both directions:
 *
 *   - No claim: a single add_to_canvas showed "Canvas ×2" and "2 tool calls"
 *     live, then "1 tool call" after a reload, where the count comes from the
 *     server's own tally.
 *   - Claim without recording: `onToolCall` claimed every id up front but only
 *     records for route_handoff and the parseToolResult tools. Ordinary tools
 *     are recorded solely by the status handler, which skips whatever is
 *     claimed — so nothing was recorded, executedToolsRef reached onFinish
 *     empty, and every live turn lost its pills. Shipped in 63b70f38.
 *
 * Both share one root: claiming and recording were separate steps and could
 * disagree. This makes them a single decision. A caller asks whether to
 * record; the id is marked only when the answer is yes. There is no way to
 * claim an id without being told to record it, which is the invariant the two
 * bugs each broke from one side.
 *
 * Callers that have nothing to record must not ask.
 */

/**
 * Should the caller record this tool execution?
 *
 * Returns true exactly once per execution, and marks the ledger only when it
 * returns true. Declining leaves the ledger untouched, so a call that is
 * turned away is never stranded as claimed-but-unrecorded.
 *
 * @param seen           shared ledger of claimed call ids, mutated in place
 * @param callId         the stream's tool-call id, straight off a stream part
 *                       and so `unknown`: absent on older parts, and the
 *                       non-string guard below is the contract, not decoration
 * @param toolName       used for the no-id fallback
 * @param recordedNames  names already recorded this turn, for that fallback
 */
export function claimForRecording(
  seen: Set<string>,
  callId: unknown,
  toolName: string,
  recordedNames: readonly string[] | undefined,
): boolean {
  // Only a non-empty string is a usable id. '' would be claimed once and then
  // block every later call that also lacks one.
  const id = typeof callId === 'string' && callId.length > 0 ? callId : undefined

  if (id) {
    if (seen.has(id)) return false
    seen.add(id)
    return true
  }

  // No id to dedup on, so fall back to the tool's name. Coarser — it collapses
  // a genuine second execution of the same tool within one turn — but it is
  // all an older part gives us, and under-counting beats double-counting here.
  return !(recordedNames ?? []).includes(toolName)
}
